import { ControlIcons } from 'owa-control-icons';
import loc from 'owa-localize';
import { isMeetingCancellation, isMeetingRequest, isMeetingResponse } from 'owa-meeting-message';
import MeetingCancellationMessageType from 'owa-service/lib/contract/MeetingCancellationMessageType';
import MeetingMessage from 'owa-service/lib/contract/MeetingMessage';
import MeetingRequestMessageType from 'owa-service/lib/contract/MeetingRequestMessageType';
import MeetingResponseMessageType from 'owa-service/lib/contract/MeetingResponseMessageType';
import {
    senderTimeUpdate,
    senderLocationUpdate,
    senderLocationTimeUpdate,
    senderSubjectUpdate,
    senderDescriptionUpdate,
    senderOnlineMeetingUpdate,
    senderGeneralUpdate,
    senderGeneralRequest,
    senderCancelled,
    senderAccepted,
    senderTentative,
    senderDeclined,
    senderTentativeProposeNewTime,
    senderDeclinedProposeNewTime,
    instanceSenderTimeUpdate,
    instanceSenderLocationUpdate,
    instanceSenderLocationTimeUpdate,
    instanceSenderSubjectUpdate,
    instanceSenderDescriptionUpdate,
    instanceSenderOnlineMeetingUpdate,
    instanceSenderGeneralUpdate,
    instanceSenderCancelled,
    instanceSenderAccepted,
    instanceSenderTentative,
    instanceSenderDeclined,
    instanceSenderTentativeProposeNewTime,
    instanceSenderDeclinedProposeNewTime,
} from './getMeetingSummaryProps.locstring.json';

export interface MeetingSummaryProps {
    icon: ControlIcons;
    senderSummary: string;
}

/*
 Returns null for cases where the meeting summary does not show. Currently shows for:
 - Meeting requests
 - Meeitng responses with A/T/D response (other options are errors, return null)
 - Meeting cancellations

 Meeting forward notifications are not currently supported and will return null
*/
export function getMeetingSummaryProps(message: MeetingMessage): MeetingSummaryProps | null {
    let summary = null;
    const itemClass = message.ItemClass;
    if (isMeetingRequest(itemClass)) {
        summary = getMeetingRequestSummaryProps(message as MeetingRequestMessageType);
    } else if (isMeetingResponse(itemClass)) {
        summary = getMeetingResponseSummaryProps(message as MeetingResponseMessageType);
    } else if (isMeetingCancellation(itemClass)) {
        summary = getMeetingCancellationSummaryProps(message as MeetingCancellationMessageType);
    }

    return summary;
}

function getMeetingRequestSummaryProps(request: MeetingRequestMessageType): MeetingSummaryProps {
    let summary = null;
    if (request.MeetingRequestType == 'NewMeetingRequest') {
        summary = {
            icon: ControlIcons.Event,
            senderSummary: loc(senderGeneralRequest),
        };
    } else {
        if (request.ChangeHighlights) {
            const {
                HasLocationChanged,
                HasEndTimeChanged,
                HasStartTimeChanged,
                HasSubjectChanged,
                HasBodyChanged,
                HasOnlineMeetingUrlChanged,
            } = request.ChangeHighlights;
            const HasTimeChanged = HasEndTimeChanged || HasStartTimeChanged;

            const changeCount =
                (HasLocationChanged ? 1 : 0) +
                (HasTimeChanged ? 1 : 0) +
                (HasSubjectChanged ? 1 : 0) +
                (HasOnlineMeetingUrlChanged ? 1 : 0);

            if (changeCount === 1) {
                if (HasLocationChanged) {
                    summary = {
                        icon: ControlIcons.Poi,
                        senderSummary: isInstanceOrException(request)
                            ? loc(instanceSenderLocationUpdate)
                            : loc(senderLocationUpdate),
                    };
                } else if (HasTimeChanged) {
                    summary = {
                        icon: ControlIcons.Clock,
                        senderSummary: isInstanceOrException(request)
                            ? loc(instanceSenderTimeUpdate)
                            : loc(senderTimeUpdate),
                    };
                } else if (HasSubjectChanged) {
                    summary = {
                        icon: ControlIcons.Event,
                        senderSummary: isInstanceOrException(request)
                            ? loc(instanceSenderSubjectUpdate)
                            : loc(senderSubjectUpdate),
                    };
                } else if (HasOnlineMeetingUrlChanged) {
                    summary = {
                        icon: ControlIcons.Event,
                        senderSummary: isInstanceOrException(request)
                            ? loc(instanceSenderOnlineMeetingUpdate)
                            : loc(senderOnlineMeetingUpdate),
                    };
                }
            } else if (changeCount === 2 && HasLocationChanged && HasTimeChanged) {
                summary = {
                    icon: ControlIcons.Event,
                    senderSummary: isInstanceOrException(request)
                        ? loc(instanceSenderLocationTimeUpdate)
                        : loc(senderLocationTimeUpdate),
                };
            } else if (changeCount === 0 && HasBodyChanged) {
                // handle body separately as it can frequently be updated with other changes
                summary = {
                    icon: ControlIcons.List,
                    senderSummary: isInstanceOrException(request)
                        ? loc(instanceSenderDescriptionUpdate)
                        : loc(senderDescriptionUpdate),
                };
            }
        }
        if (!summary) {
            summary = {
                icon: ControlIcons.Event,
                senderSummary: isInstanceOrException(request)
                    ? loc(instanceSenderGeneralUpdate)
                    : loc(senderGeneralUpdate),
            };
        }
    }
    return summary;
}

function getMeetingResponseSummaryProps(
    response: MeetingResponseMessageType
): MeetingSummaryProps | null {
    let summary = null;
    const hasTimeProposal = response.ProposedStart && response.ProposedEnd;
    if (response.ResponseType == 'Accept') {
        summary = {
            icon: ControlIcons.EventAccepted,
            senderSummary: isInstanceOrException(response)
                ? loc(instanceSenderAccepted)
                : loc(senderAccepted),
        };
    } else if (response.ResponseType == 'Tentative') {
        if (hasTimeProposal) {
            summary = {
                icon: ControlIcons.EventTentative,
                senderSummary: isInstanceOrException(response)
                    ? loc(instanceSenderTentativeProposeNewTime)
                    : loc(senderTentativeProposeNewTime),
            };
        } else {
            summary = {
                icon: ControlIcons.EventTentative,
                senderSummary: isInstanceOrException(response)
                    ? loc(instanceSenderTentative)
                    : loc(senderTentative),
            };
        }
    } else if (response.ResponseType == 'Decline') {
        if (hasTimeProposal) {
            summary = {
                icon: ControlIcons.EventDeclined,
                senderSummary: isInstanceOrException(response)
                    ? loc(instanceSenderDeclinedProposeNewTime)
                    : loc(senderDeclinedProposeNewTime),
            };
        } else {
            summary = {
                icon: ControlIcons.EventDeclined,
                senderSummary: isInstanceOrException(response)
                    ? loc(instanceSenderDeclined)
                    : loc(senderDeclined),
            };
        }
    }
    return summary;
}

function getMeetingCancellationSummaryProps(
    cancellation: MeetingCancellationMessageType
): MeetingSummaryProps {
    return {
        icon: ControlIcons.RemoveEvent,
        senderSummary: isInstanceOrException(cancellation)
            ? loc(instanceSenderCancelled)
            : loc(senderCancelled),
    };
}

function isInstanceOrException(
    message: MeetingRequestMessageType | MeetingCancellationMessageType | MeetingResponseMessageType
): boolean {
    /**
     * Ideally, we would just check CalendarItemType but it seems broken.
     * 'Single' is returned in the 'Instance' case. To account for this, we check for
     * the presence of a RecurrenceId. If the item has a ReccurenceId, we are dealing
     * with an instance of a series
     **/
    return message.CalendarItemType === 'Single' && !!message.RecurrenceId;
}
